import React, { FC, useMemo, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import { AuthTabs } from "@app/components";
import SignIn from "../signIn";
import SignUp from "../signUp";
import { defineMessages, useIntl } from "react-intl";
import { usePortalRedirects, useTokenSourceFromQuery } from "@app/hooks";
import DateOfBirthVerification from "../../../pages/private/dateOfBirthVerification";
import { ProfileProvider, UppAuthContext } from "@app/provider";
import useTokenizedLink from "@app/hooks/useTokenizedLinks";
import { AccountNotificationTemplate } from "@app/hooks/useGeneratedTokenizedLink";
import { useQuery } from "@app/utils";

import "./auth.scss";

const messages = defineMessages({
  authLoginTab: {
    id: "authLoginTab",
    defaultMessage: "Login",
  },
  authCreateAccountTab: {
    id: "authCreateAccountTab",
    defaultMessage: "Create account",
  },
});

const Auth: FC = () => {
  const intl = useIntl();
  const location = useLocation();
  const { toLogin, toSignUp } = usePortalRedirects();
  const { token, tokenNotificationType } = useTokenSourceFromQuery();

  const { isUserExistData } = useTokenizedLink();
  const {
    isVerifiedProfileByToken,
    setVerifiedProfileByToken,
    rejectedIntentHandle,
  } = useContext(UppAuthContext);

  // UPP-5865: skip the DOB screen when a guest-flow DOB-proof handle rides in as
  // `si`, unless the server already rejected it this session. See useSignupIntent.
  const query = useQuery();
  const si = query.get("si");
  const siActive = !!si && si !== rejectedIntentHandle;

  // Latches once DOB is verified so a transient re-render doesn't flash the DOB
  // screen again. UPP-5864: an explicit reset to `false` (e.g. sign-up rejected
  // for a stale/invalid DOB proof) must re-open the gate so the user can
  // re-verify; `undefined` (never verified) leaves the latch untouched.
  const hasVerifiedDob = useRef(false);
  if (isVerifiedProfileByToken) {
    hasVerifiedDob.current = true;
  } else if (isVerifiedProfileByToken === false) {
    hasVerifiedDob.current = false;
  }

  const TABS = [
    {
      key: "log_in",
      label: intl.formatMessage(messages.authLoginTab),
      component: <SignIn />,
    },
    {
      key: "sign_up",
      label: intl.formatMessage(messages.authCreateAccountTab),
      component: <SignUp />,
    },
  ];

  const DEFAULT_TAB_KEY = TABS[0].key; // "log_in"

  const currentTabKey = useMemo(() => {
    const pathSegment = location.pathname.split("/").filter(Boolean)[0];
    return TABS.some((tab) => tab.key === pathSegment)
      ? pathSegment
      : DEFAULT_TAB_KEY;
  }, [location.pathname]);

  const handleTabChange = (key: string) => {
    key === "sign_up"
      ? toSignUp({ preserveQuery: false })
      : toLogin({ preserveQuery: false });
  };

  if (
    !isVerifiedProfileByToken &&
    !hasVerifiedDob.current &&
    !siActive &&
    token &&
    !isUserExistData &&
    tokenNotificationType !== AccountNotificationTemplate.ACTIVATE_YOUR_ACCOUNT
  ) {
    return (
      <ProfileProvider>
        <DateOfBirthVerification onSubmit={setVerifiedProfileByToken} />
      </ProfileProvider>
    );
  }

  return (
    <div className="auth__container">
      <AuthTabs
        tabs={TABS}
        activeTab={currentTabKey}
        onTabChange={handleTabChange}
      />
    </div>
  );
};

export default Auth;
